import palette from './colorPalette';

interface RGB {
  r: number;
  g: number;
  b: number;
}

interface Theme {
  readonly utils: {
    rgba: (hex: string, alpha: number) => string;
  };
  readonly palette: typeof palette;
  readonly fonts: {
    readonly primary: string;
    readonly pre: string;
    readonly quote: string;
  };
  readonly sizes: {
    readonly DSMaxWidth: string;
    readonly header: string;
  };
  readonly spacing: {
    readonly xs: string;
    readonly s: string;
    readonly m1: string;
    readonly m2: string;
    readonly m3: string;
    readonly l: string;
    readonly xl: string;
    readonly '2xl': string;
    readonly '3xl': string;
  };
  readonly breakpoints: {
    readonly mobile: string;
    readonly tablet: string;
    readonly tabletOnly: string;
    readonly desktop: string;
  };
  readonly media: {
    readonly narrowPhone: string;
    readonly w500: string;
  };
}

const hexToRgb = (hex: string): RGB | null => {
  // http://stackoverflow.com/a/5624139
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const replacedHex = hex.replace(
    shorthandRegex,
    (_, r, g, b) => r + r + g + g + b + b
  );

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(replacedHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const theme: Theme = {
  utils: {
    rgba: (hex: string, alpha: number): string => {
      const color = hexToRgb(hex);
      if (!color) {
        console.warn(`Invalid hex color: ${hex}`);
        return `rgba(0, 0, 0, ${alpha})`;
      }
      return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    },
  },
  palette,
  fonts: {
    primary: 'proxima-nova, sans-serif',
    pre: 'Consolas, Liberation Mono, Menlo, Courier, monospace',
    quote: 'Georgia, serif',
  },
  sizes: {
    DSMaxWidth: '80rem' /* 1280px */,
    header: '3.875rem' /* 62px */,
  },
  spacing: {
    xs: '0.25rem', // 4px
    s: '0.5rem', // 8px
    m1: '1rem', // 16px
    m2: '1.5rem', // 24px
    m3: '2rem', // 32px
    l: '3rem', // 48px
    xl: '4rem', // 64px
    '2xl': '6rem', // 96px
    '3xl': '8rem', // 128px
  },
  breakpoints: {
    mobile: '@media screen and (max-width: 767px)',
    tablet: '@media screen and (max-width: 1023px)',
    tabletOnly: '@media screen and (min-width: 768px) and (max-width: 1023px)',
    desktop: '@media screen and (min-width: 1024px)',
  },
  media: {
    narrowPhone: '@media (max-width: 321px)',
    w500: '@media (max-width: 500px)',
  },
};

export default theme;
export type { Theme };
